import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { siteConfig } from "@/config/siteConfig";
import { Loader2 } from "lucide-react";

const AdminLogin = () => {
  const { login, isAuthenticated, checking } = useAdminAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const nextPath = params.get("next") || "/admin";

  useEffect(() => {
    if (!checking && isAuthenticated) {
      navigate(nextPath, { replace: true });
    }
  }, [checking, isAuthenticated, nextPath, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate(nextPath, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) setError("Invalid username or password.");
      else if (status === 429) setError("Too many attempts — try again in a few minutes.");
      else setError("Something went wrong — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Admin Sign-in — ${siteConfig.COMPANY_NAME}`}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-alt)] flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="font-heading text-2xl font-semibold text-[var(--navy)] tracking-tight" data-testid="admin-login-brand">
              {siteConfig.COMPANY_NAME}
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--pharma-blue)] mt-2">
              Admin Console
            </div>
          </div>

          <div className="bg-white border border-[var(--border)] rounded-[4px] p-6 sm:p-8">
            <h1 className="font-heading text-xl font-semibold text-[var(--navy)] mb-6">
              Sign in to admin
            </h1>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-login-form">
              <div>
                <label htmlFor="username" className="block text-xs font-medium uppercase tracking-wider text-[var(--slate)] mb-2">
                  Username
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={submitting}
                  className="w-full px-3 py-2.5 border border-[var(--border)] rounded-[4px] focus:outline-none focus:border-[var(--pharma-blue)] text-sm"
                  data-testid="admin-login-username"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium uppercase tracking-wider text-[var(--slate)] mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="w-full px-3 py-2.5 border border-[var(--border)] rounded-[4px] focus:outline-none focus:border-[var(--pharma-blue)] text-sm"
                  data-testid="admin-login-password"
                />
              </div>

              {error && (
                <div
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-[3px] px-3 py-2"
                  role="alert"
                  data-testid="admin-login-error"
                >
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="admin-login-submit"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : "Sign in"}
              </button>
            </form>
          </div>

          <div className="text-center mt-6">
            <Link to="/" className="text-xs text-[var(--slate)] hover:text-[var(--navy)] underline underline-offset-4">
              ← Return to site
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
